import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { formatDateInTimezone } from "../../../utils/formatDateInTimezone";
import "../../../assets/css/policies.css";
import config from "../../../config";
import {
  acknowledgePolicy,
  getEmployeePolicies,
} from "../../../services/policyServices";
import PolicyAcknowledgmentModal from "../../../components/modelpopup/PolicyAcknowledgmentModal";
import Swal from "sweetalert2";

const MyPolicies = () => {
  const [policyData, setPolicyData] = useState([]);
  const [filteredPolicyData, setFilteredPolicyData] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [loader, setLoader] = useState(false);

  const loginEmployee = localStorage.getItem("employeeID");

  const fetchPolicyData = async () => {
    try {
      const res = await getEmployeePolicies(loginEmployee);
      const policies = res?.results;
      setPolicyData(policies);
      setFilteredPolicyData(policies);
      const userManualCount = policies?.filter(
        (policy) => policy?.policy_category_name === "User Manual"
      ).length;
      const allPoliciesCount = policies.length;
      const companyPoliciesCount = policies?.filter(
        (policy) => policy?.policy_category_name === "Company Policies"
      ).length;

      setStatsData([
        {
          id: 1,
          title: "All Policies",
          value: allPoliciesCount.toString(),
          active: true,
        },
        {
          id: 2,
          title: "User Manual",
          value: userManualCount.toString(),
          active: false,
        },
        {
          id: 3,
          title: "Company Policies",
          value: companyPoliciesCount.toString(),
          active: false,
        },
      ]);
    } catch (error) {
      console.error("Error fetching policy data:", error);
    }
  };

  useEffect(() => {
    if (loginEmployee) {
      fetchPolicyData();
    }
  }, []);

  const filterPolicies = (policyType) => {
    if (policyType === "All Policies") {
      setFilteredPolicyData(policyData);
    } else {
      const filteredPolicies = policyData?.filter(
        (policy) => policy?.policy_category_name === policyType
      );
      setFilteredPolicyData(filteredPolicies);
    }

    const updatedStatsData = statsData.map((stat) => ({
      ...stat,
      active: stat?.title === policyType,
    }));
    setStatsData(updatedStatsData);
  };

  const handleDownload = async (filePath) => {
    try {
      const a = document.createElement("a");
      a.href = filePath;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const closeAcknowledgementModal = () => {
    setModalVisible(false);
    setSelectedPolicy(null);
  };

  const handleAcknowledgePolicy = async () => {
    setLoader(true);
    try {
      if (!loginEmployee) {
        console.error("Login employee ID is missing.");
        return;
      }

      if (!selectedPolicy?.policy_id || !selectedPolicy?.policy_version_id) {
        console.error("Selected policy details are incomplete.");
        return;
      }

      const result = await acknowledgePolicy({
        employeeId: loginEmployee,
        policyId: selectedPolicy?.policy_id,
        policyVersionId: selectedPolicy?.policy_version_id,
      });

      if (result?.success === true) {
        closeAcknowledgementModal();
        Swal.fire({
          title: "Policy",
          text: "Acknowledged successfully!",
          icon: "success",
          timer: 1500,
          timerProgressBar: true,
        });
        await fetchPolicyData();
      } else {
        console.error("Failed to acknowledge policy.");
      }
    } catch (error) {
      console.error("Error acknowledging policy:", error);
      Swal.fire({
        title: "Policy",
        text: "Something went wrong!",
        icon: "warning",
        timer: 1500,
        timerProgressBar: true,
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="My Policies"
            title="Dashboard"
            subtitle="My Policies"
            modal="#add_policy"
            name="Add Policy"
          />
          {!statsData?.length && !filteredPolicyData?.length ? (
            <div className="text-center mt-5">
              <h4>No Policies Available</h4>
            </div>
          ) : null}
          <div className="row mb-4">
            {statsData?.map((stat) => (
              <div
                className={`col-md-4 d-flex`}
                key={stat?.id}
                onClick={() => filterPolicies(stat?.title)}
                style={{ cursor: "pointer" }}
              >
                <div
                  className={`stats-info w-100 ${
                    stat?.active ? "stats-bg" : ""
                  }`}
                >
                  <h6 className={` ${stat.active ? "text-white" : ""}`}>
                    {stat?.title}
                  </h6>
                  <h4 className={` ${stat.active ? "text-white" : ""}`}>
                    {stat?.value}
                  </h4>
                </div>
              </div>
            ))}
          </div>

          {filteredPolicyData?.map((item) => {
            const isAcknowledged =
              item.acknowledgment_status === "ACKNOWLEDGED";
            const statusColor = isAcknowledged
              ? "text-success"
              : "text-warning";
            const statusText = isAcknowledged ? "Acknowledged" : "Pending";
            const statusIcon = isAcknowledged
              ? "fas fa-check-circle"
              : "fas fa-hourglass-half";

            return (
              <div
                className="card shadow p-3 mb-4 bg-white rounded policies-left-border"
                key={item.id}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-9">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="mb-1">{item.policy_name}</h5>
                          <div className="mb-2 text-muted">
                            Last update on:{" "}
                            {formatDateInTimezone(item.created_at)}
                          </div>
                          <div className="mb-2">
                            <span
                              className="px-2 py-1"
                              style={{
                                backgroundColor: "var(--company-theme)",
                                borderRadius: "10px",
                                color: "white",
                                fontSize: "0.75rem",
                              }}
                            >
                              {item.policy_category_name}
                            </span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <span className="me-1 text-muted">
                              Acknowledgement:
                            </span>
                            <button
                              className={`btn border-0 p-0 ml-2 ${statusColor}`}
                              onClick={() => {
                                setModalVisible(true);
                                setSelectedPolicy(item);
                              }}
                            >
                              <i
                                className={`${statusIcon} mr-1 me-1`}
                                title={statusText}
                              ></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 d-flex justify-content-end align-items-start">
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() =>
                            handleDownload(
                              `${config.IMAGE_URL}${item.policy_file}`
                            )
                          }
                        >
                          <i
                            className="fas fa-download text-primary"
                            title="Download policy"
                          ></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() =>
                            handleDownload(
                              `${config.IMAGE_URL}${item.policy_file}`
                            )
                          }
                        >
                          <i
                            className="fas fa-eye text-primary"
                            title="View policy"
                          ></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => {
                            setModalVisible(true);
                            setSelectedPolicy(item);
                          }}
                        >
                          <i
                            className={`fas fa-file-alt ${statusColor}`}
                            title="Acknowledge policy"
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {modalVisible && (
          <PolicyAcknowledgmentModal
            show={modalVisible}
            onClose={closeAcknowledgementModal}
            onSubmit={handleAcknowledgePolicy}
            policyDocument={selectedPolicy?.policy_file}
            title={selectedPolicy ? selectedPolicy?.policy_name : "Policy"}
            viewOnly={
              selectedPolicy?.acknowledgment_status === "ACKNOWLEDGED"
                ? true
                : false
            }
            acknowledgmentLabel={`Yes, I have read and accept the ${selectedPolicy?.policy_name}.`}
            delaySeconds={5}
            loader={loader}
          />
        )}
      </div>
    </>
  );
};

export default MyPolicies;
